import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useStableAuth } from '@/hooks/useStableAuth';
import { supabase } from '@/integrations/supabase/client';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  required: boolean;
  category: 'profile' | 'security' | 'features' | 'project';
}

interface UserOnboardingContextType {
  steps: OnboardingStep[];
  currentStep: OnboardingStep | null;
  completedSteps: string[];
  isOnboardingComplete: boolean;
  isOnboardingActive: boolean;
  progress: number;
  completeStep: (stepId: string) => void;
  skipStep: (stepId: string) => void;
  startOnboarding: () => void;
  endOnboarding: () => void;
  restartOnboarding: () => void;
  getNextStep: () => OnboardingStep | null;
}

const UserOnboardingContext = createContext<UserOnboardingContextType | undefined>(undefined);

const defaultSteps: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Clazma',
    description: 'Get started with your AI-powered business platform',
    completed: false,
    required: true,
    category: 'profile'
  },
  {
    id: 'profile_setup',
    title: 'Complete Your Profile',
    description: 'Add your business information and preferences',
    completed: false,
    required: true,
    category: 'profile'
  },
  {
    id: 'security_setup',
    title: 'Secure Your Account',
    description: 'Enable two-factor authentication for enhanced security',
    completed: false,
    required: true,
    category: 'security'
  },
  {
    id: 'explore_features',
    title: 'Discover Key Features',
    description: 'Learn about our AI tools and business solutions',
    completed: false,
    required: false,
    category: 'features'
  },
  {
    id: 'first_project',
    title: 'Create Your First Project',
    description: 'Start building with our project wizard',
    completed: false,
    required: false,
    category: 'project'
  },
  {
    id: 'explore_dashboard',
    title: 'Explore Your Dashboard',
    description: 'Get familiar with your personalized workspace',
    completed: false,
    required: false,
    category: 'features'
  }
];

interface UserOnboardingProviderProps {
  children: ReactNode;
}

export const UserOnboardingProvider: React.FC<UserOnboardingProviderProps> = ({ children }) => {
  const { user } = useStableAuth();
  const [steps, setSteps] = useState<OnboardingStep[]>(defaultSteps);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [isOnboardingActive, setIsOnboardingActive] = useState(false);

  // Load user's onboarding progress and check if should show onboarding
  useEffect(() => {
    try {
      if (user?.id) {
        loadOnboardingProgress();
      } else {
        // For anonymous users, only show onboarding if it's their first visit
        const hasVisited = localStorage.getItem('clazma_visited');
        if (!hasVisited) {
          localStorage.setItem('clazma_visited', 'true');
          setIsOnboardingActive(true);
        } else {
          setIsOnboardingActive(false);
        }
      }
    } catch (error) {
      console.error('Error in onboarding useEffect:', error);
      setIsOnboardingActive(false);
    }
  }, [user]);

  const loadOnboardingProgress = async () => {
    if (!user?.id) {
      console.log('No user ID available for onboarding progress');
      return;
    }

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id as any)
        .maybeSingle();

      if (error) {
        console.error('Database error loading onboarding progress:', error);
        setIsOnboardingActive(false);
        return;
      }

      if (profile) {
        const permissions = (profile as any).permissions as any || {};
        const onboardingData = permissions.onboarding || {};
        const completed = onboardingData.completedSteps || [];
        const isActive = onboardingData.isActive !== false; // Default to true for new users
        
        setCompletedSteps(completed);
        setIsOnboardingActive(isActive);
        
        // Update steps with completion status
        setSteps(prev => prev.map(step => ({
          ...step,
          completed: completed.includes(step.id)
        })));
      } else {
        // No profile found, set defaults for new user
        setIsOnboardingActive(true);
        setCompletedSteps([]);
      }
    } catch (error) {
      console.error('Error loading onboarding progress:', error);
      setIsOnboardingActive(false);
    }
  };

  const saveOnboardingProgress = async (newCompletedSteps: string[], active: boolean) => {
    if (!user?.id) {
      console.log('No user ID available for saving onboarding progress');
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          permissions: {
            ...user?.user_metadata,
            onboarding: {
              completedSteps: newCompletedSteps,
              isActive: active,
              lastUpdated: new Date().toISOString()
            }
          }
        } as any)
        .eq('id', user.id as any);

      if (error) {
        console.error('Database error saving onboarding progress:', error);
      }
    } catch (error) {
      console.error('Error saving onboarding progress:', error);
    }
  };

  const completeStep = (stepId: string) => {
    const newCompletedSteps = [...completedSteps, stepId];
    setCompletedSteps(newCompletedSteps);
    
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed: true } : step
    ));

    saveOnboardingProgress(newCompletedSteps, isOnboardingActive);

    // Track completion analytics safely
    import('@/utils/safeAnalytics').then(({ trackOnboardingStep }) => {
      trackOnboardingStep(stepId, 'completed', steps.find(s => s.id === stepId)?.category);
    }).catch(() => {
      // Fallback - no analytics in case of import failure
    });
  };

  const skipStep = (stepId: string) => {
    // Track skip analytics safely
    import('@/utils/safeAnalytics').then(({ trackOnboardingStep }) => {
      trackOnboardingStep(stepId, 'skipped', steps.find(s => s.id === stepId)?.category);
    }).catch(() => {
      // Fallback - no analytics in case of import failure
    });
  };

  const startOnboarding = () => {
    setIsOnboardingActive(true);
    saveOnboardingProgress(completedSteps, true);
  };

  const endOnboarding = () => {
    setIsOnboardingActive(false);
    saveOnboardingProgress(completedSteps, false);
  };

  const restartOnboarding = () => {
    const resetSteps = steps.map(step => ({ ...step, completed: false }));
    setSteps(resetSteps);
    setCompletedSteps([]);
    setIsOnboardingActive(true);
    saveOnboardingProgress([], true);
  };

  const getCurrentStep = (): OnboardingStep | null => {
    return steps.find(step => !step.completed) || null;
  };

  const getNextStep = (): OnboardingStep | null => {
    const currentStep = getCurrentStep();
    if (!currentStep) return null;
    
    const currentIndex = steps.findIndex(step => step.id === currentStep.id);
    return steps[currentIndex + 1] || null;
  };

  const progress = (completedSteps.length / steps.length) * 100;
  const isOnboardingComplete = completedSteps.length === steps.filter(s => s.required).length;

  const value: UserOnboardingContextType = {
    steps,
    currentStep: getCurrentStep(),
    completedSteps,
    isOnboardingComplete,
    isOnboardingActive,
    progress,
    completeStep,
    skipStep,
    startOnboarding,
    endOnboarding,
    restartOnboarding,
    getNextStep
  };

  return (
    <UserOnboardingContext.Provider value={value}>
      {children}
    </UserOnboardingContext.Provider>
  );
};

export const useUserOnboarding = () => {
  const context = useContext(UserOnboardingContext);
  if (context === undefined) {
    throw new Error('useUserOnboarding must be used within a UserOnboardingProvider');
  }
  return context;
};